import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import { Notice, StatusBadge } from '../ui/Badge';
import { Button } from '../ui/Button';
import {
  StaleNotice,
  UnresolvedState,
  counted,
  refreshFailed,
  shownState,
  unresolved,
} from '../ui/QueryStates';
import { field } from '../create/Field';
import { IconChecklist, IconTrash, IconPlus } from '../icons';

type Draft = { label: string; required: boolean };
type Template = {
  id: string;
  name: string;
  description: string | null;
  items: { id: string; label: string; required: boolean }[];
};

const blank = (): Draft[] => [{ label: '', required: false }];

/**
 * Where checklists are built (PRD v2 §2). The whole editor is a list of lines with one
 * toggle each, because a shop owner writing "AC Tune-Up" at 9pm should be typing, not
 * learning a form builder. Required is the only property that changes behaviour, so it's
 * the only property on offer.
 *
 * Editing shapes future stops only — visits already carrying the form keep their copies.
 */
export function ChecklistsCard({ id }: { id: string }) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const templates = useQuery(trpc.checklist.list.queryOptions());
  const [editing, setEditing] = useState<Template | 'new' | null>(null);

  const remove = useMutation(
    trpc.checklist.delete.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  // `shownState`, not `queryState`: a failed REFRESH is not a reason to throw away forty
  // checklists that are correct and on screen. See the docblock there (042).
  const state = shownState(templates, templates.data);

  return (
    // The body branched correctly and the TITLE did not, so the card rendered "Loading…"
    // underneath a heading that had already announced "(0)" — and on a failure it told a
    // shop with forty checklists that it had none. The heading is the half people read.
    <SectionCard
      id={id}
      title={counted('Checklists', state, templates.data?.length ?? 0)}
    >
      {!templates.data ? (
        <UnresolvedState
          state={unresolved(state)}
          what="your checklists"
          icon={IconChecklist}
          error={templates.error}
          onRetry={() => void templates.refetch()}
        />
      ) : (
        <>
          <StaleNotice
            show={refreshFailed(templates, templates.data)}
            onRetry={() => void templates.refetch()}
          />
          {remove.error ? <Notice tone="danger">{remove.error.message}</Notice> : null}

          {templates.data.length === 0 ? (
            <p className="mb-3 text-sm text-muted-foreground">
              None yet. A checklist is the list a tech works at the door — and steps you
              mark <strong>required</strong> stop them finishing the stop without doing
              them.
            </p>
          ) : (
            <ul className="mb-3 divide-y divide-border text-sm">
              {templates.data.map((t) => {
                const required = t.items.filter((i) => i.required).length;
                return (
                  <li key={t.id} className="flex items-center gap-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{t.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {t.items.length} step{t.items.length === 1 ? '' : 's'}
                        {required > 0 ? ` · ${required} required` : ''}
                      </p>
                    </div>
                    {required > 0 ? (
                      <StatusBadge tone="warn">Blocks finish</StatusBadge>
                    ) : null}
                    <Button variant="ghost" onClick={() => setEditing(t as Template)}>
                      Edit
                    </Button>
                    <button
                      type="button"
                      aria-label={`Delete checklist ${t.name}`}
                      onClick={() =>
                        remove.mutate({ checklistTemplateId: t.id, ...idem() })
                      }
                      className="rounded-[var(--radius-sm)] p-1 text-muted-foreground hover:bg-surface-2 hover:text-[var(--sw-danger-text)]"
                    >
                      <IconTrash size={14} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {editing ? (
            <ChecklistEditor
              template={editing === 'new' ? null : editing}
              onDone={() => setEditing(null)}
            />
          ) : (
            <Button variant="ghost" onClick={() => setEditing('new')}>
              <IconPlus size={14} />
              New checklist
            </Button>
          )}
        </>
      )}
    </SectionCard>
  );
}

function ChecklistEditor({
  template,
  onDone,
}: {
  template: Template | null;
  onDone: () => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const [name, setName] = useState(template?.name ?? '');
  const [items, setItems] = useState<Draft[]>(
    template?.items.map((i) => ({ label: i.label, required: i.required })) ?? blank(),
  );

  const onSuccess = () => {
    void qc.invalidateQueries();
    onDone();
  };
  const create = useMutation(trpc.checklist.create.mutationOptions({ onSuccess }));
  const update = useMutation(trpc.checklist.update.mutationOptions({ onSuccess }));
  const busy = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  const filled = items
    .map((i) => ({ label: i.label.trim(), required: i.required }))
    .filter((i) => i.label.length > 0);
  const canSave = name.trim().length > 0 && filled.length > 0 && !busy;

  const patch = (i: number, next: Partial<Draft>) =>
    setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...next } : it)));

  const save = () => {
    if (!canSave) return;
    if (template) {
      update.mutate({
        checklistTemplateId: template.id,
        name: name.trim(),
        items: filled,
        ...idem(),
      });
    } else {
      create.mutate({ name: name.trim(), items: filled, ...idem() });
    }
  };

  return (
    <div className="space-y-3 rounded-[var(--radius)] border border-border bg-surface-2 p-3">
      {error ? <Notice tone="danger">{error.message}</Notice> : null}

      <input
        className={field}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Checklist name — e.g. AC Tune-Up"
        aria-label="Checklist name"
        autoFocus
      />

      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-center gap-2">
            <input
              className={`${field} flex-1`}
              value={item.label}
              onChange={(e) => patch(i, { label: e.target.value })}
              onKeyDown={(e) => {
                // Enter adds the next line — this is a list you type straight down.
                if (e.key === 'Enter') {
                  e.preventDefault();
                  setItems((prev) => [
                    ...prev.slice(0, i + 1),
                    { label: '', required: false },
                    ...prev.slice(i + 1),
                  ]);
                }
              }}
              placeholder={i === 0 ? 'First step…' : 'Next step…'}
              aria-label={`Step ${i + 1}`}
            />
            <label className="flex shrink-0 items-center gap-1.5 text-xs">
              <input
                type="checkbox"
                checked={item.required}
                onChange={(e) => patch(i, { required: e.target.checked })}
                aria-label={`Step ${i + 1} is required`}
              />
              Required
            </label>
            <button
              type="button"
              aria-label={`Remove step ${i + 1}`}
              disabled={items.length === 1}
              onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
              className="rounded-[var(--radius-sm)] p-1 text-muted-foreground hover:bg-surface hover:text-[var(--sw-danger-text)] disabled:opacity-40"
            >
              <IconTrash size={14} />
            </button>
          </li>
        ))}
      </ul>

      <Button
        variant="ghost"
        onClick={() => setItems((prev) => [...prev, { label: '', required: false }])}
      >
        <IconPlus size={14} />
        Add a step
      </Button>

      <p className="text-xs text-muted-foreground">
        A <strong>required</strong> step blocks the tech from finishing the stop until
        it's done. Editing this list only affects visits scheduled from now on — stops
        already carrying it keep what the tech was given.
      </p>

      <div className="flex gap-2">
        <Button disabled={!canSave} onClick={save}>
          {busy ? 'Saving…' : template ? 'Save changes' : 'Create checklist'}
        </Button>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
